import React, { useEffect, useState } from "react";
import { Collapse } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ScadentarDetails from "./scadentarDetails";
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Chip,
  Select,
  MenuItem,
  IconButton,
  TextField,
  Menu,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import { Grid, Card, CardContent } from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { TASKS_API_BASE } from "./apiConfig";
import {
  MonetizationOn,
  EventNote,
  EventAvailable,
  NewReleases,
  ArrowUpward,
  ArrowDownward,
  ChangeHistory,
  Assignment,
  Edit,
  MoreVert,
  FilterList,
  ImportExport,
  Sort,
  Close,
  ArrowRight,
  TaskAlt,
  CancelOutlined,
  SyncRounded,
} from "@mui/icons-material";

const TASKS_API_BASE_URL = TASKS_API_BASE;
const INITIAL_VISIBLE_ROWS = 80;
const VISIBLE_ROWS_INCREMENT = 80;
const PAYMENT_STATUS_OPTIONS = [
  "Planificat",
  "Depasit",
  "Achitat",
  "Returnat",
  "Anulat",
  "Whitelist",
];
const DEFAULT_HIDDEN_PAYMENT_STATUSES = new Set([
  "achitat",
  "anulat",
  "returnat",
  "whitelist",
]);

const getScadentarKey = (row) =>
  row?.issue_key || row?.id || row?.issueId || row?.issue_id || null;

const getPaymentStatus = (row) => String(row?.statusPlata ?? "").trim();
const normalizeSearchText = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const normalizeListPaymentRow = (row) => {
  const paymentStatus = String(row?.statusPlata ?? row?.status ?? "").trim();
  return {
    ...row,
    statusPlata: paymentStatus,
    status: paymentStatus,
    loadedStatusPlata: paymentStatus,
    jiraStatus: row?.jiraStatus ?? row?.jira_status ?? null,
    sumaRenegociata: row?.sumaRenegociata ?? row?.suma_renegociata ?? null,
    sumaPlataExacta: row?.sumaPlataExacta ?? row?.sumaPlata ?? null,
    deadlinePlataExacta: row?.deadlinePlataExacta ?? row?.deadlinePlata ?? null,
  };
};

const Scadentar = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [selectedRow, setSelectedRow] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [pageSnackbar, setPageSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [dashboardOpen, setDashboardOpen] = useState(false);
  const [paymentPlannedCheck, setPaymentPlannedCheck] = useState({
    loading: true,
    healthy: null,
    totalProblems: 0,
    error: null,
  });
  // Săptămână filter (multiple selection)
  const [weekFilter, setWeekFilter] = useState([]);
  // Status filter
  const [statusFilter, setStatusFilter] = useState([]);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [visibleRowCount, setVisibleRowCount] = useState(INITIAL_VISIBLE_ROWS);
  const handleClosePageSnackbar = (_, reason) => {
    if (reason === "clickaway") return;
    setPageSnackbar((prev) => ({ ...prev, open: false }));
  };
  const handleSort = (key) => {
    setSortConfig((prev) => {
      const direction =
        prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction };
    });
  };

  // ISO week number (UTC, Monday=1..Sunday=7). Avoids TZ off-by-one.
  const isoWeekUTC = (dateInput) => {
    const d = new Date(dateInput);
    if (isNaN(d)) return null;
    // convert to UTC date (strip time, keep calendar day)
    const utc = new Date(
      Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
    );
    // day index: Mon=0..Sun=6
    const day = (utc.getUTCDay() + 6) % 7;
    // move to Thursday of this week
    utc.setUTCDate(utc.getUTCDate() - day + 3);
    // first Thursday of ISO year
    const firstThursday = new Date(Date.UTC(utc.getUTCFullYear(), 0, 4));
    const firstDay = (firstThursday.getUTCDay() + 6) % 7;
    firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDay + 3);
    // weeks since first Thursday
    const week =
      1 + Math.round((utc - firstThursday) / (7 * 24 * 60 * 60 * 1000));
    return week;
  };

  // Filtrare inițială pe statusPlata, fără fallback la statusul Jira.
  const planificateRows = React.useMemo(
    () =>
      rows.filter(
        (row) =>
          ["planificat", "depasit"].includes(
            getPaymentStatus(row).toLowerCase()
          ) && typeof row.saptamana === "number"
      ),
    [rows]
  );

  const availableWeeks = React.useMemo(
    () =>
      Array.from(new Set(planificateRows.map((row) => row.saptamana))).sort(
        (a, b) => a - b
      ),
    [planificateRows]
  );

  // Statuses available for filtering
  const availableStatuses = React.useMemo(
    () =>
      Array.from(
        new Set([
          ...PAYMENT_STATUS_OPTIONS,
          ...rows.map((row) => getPaymentStatus(row)),
        ])
      ).filter(Boolean),
    [rows]
  );

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const normalizedSearchTerm = normalizeSearchText(searchTerm);
  const isSearchActive = normalizedSearchTerm.length > 0;

  // Filtrare combinată cu weekFilter (multiple), statusFilter și searchTerm
  const filteredRows = React.useMemo(
    () =>
      rows.filter((row) => {
        const saptamanaOk =
          isSearchActive ||
          !weekFilter ||
          weekFilter.length === 0 ||
          (typeof row.saptamana === "number" &&
            weekFilter.includes(row.saptamana));
        const statusCurent = getPaymentStatus(row).toLowerCase();
        const statusOk =
          isSearchActive ||
          statusFilter.length === 0 ||
          statusFilter.includes(statusCurent);

        const searchFields = [
          row.customfield_10067 || row.vanzator,
          row.summary || row.produs,
          row.customfield_10066 || row.nr_jurnal,
          row.customfield_10083 || row.sku,
          row.customfield_10069 || row.email,
          row.customfield_10068 || row.telefon,
          row.customfield_10086 || row.nr_contract,
        ].map(normalizeSearchText);

        const searchOk =
          !isSearchActive ||
          searchFields.some((value) => value.includes(normalizedSearchTerm));

        return saptamanaOk && statusOk && searchOk;
      }),
    [rows, isSearchActive, weekFilter, statusFilter, normalizedSearchTerm]
  );

  const displayRows = React.useMemo(() => {
    const baseRows =
      weekFilter && weekFilter.length > 0
        ? [...filteredRows]
        : [...filteredRows].sort((a, b) => {
            const isAOverdue = getPaymentStatus(a) === "Depasit" ? 1 : 0;
            const isBOverdue = getPaymentStatus(b) === "Depasit" ? 1 : 0;
            return isBOverdue - isAOverdue;
          });

    if (!sortConfig.key) return baseRows;

    return [...baseRows].sort((a, b) => {
      const aValue = normalizeSearchText(a[sortConfig.key]);
      const bValue = normalizeSearchText(b[sortConfig.key]);
      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredRows, sortConfig, weekFilter]);

  const visibleRows = React.useMemo(
    () => displayRows.slice(0, visibleRowCount),
    [displayRows, visibleRowCount]
  );

  useEffect(() => {
    setVisibleRowCount(INITIAL_VISIBLE_ROWS);
  }, [normalizedSearchTerm, weekFilter, statusFilter, sortConfig]);

  const handleRowsScroll = (event) => {
    const element = event.currentTarget;
    const distanceToBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight;

    if (distanceToBottom > 240) return;

    setVisibleRowCount((count) =>
      count >= displayRows.length
        ? count
        : Math.min(count + VISIBLE_ROWS_INCREMENT, displayRows.length)
    );
  };
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCreat: 0,
    totalPlanificatLuna: 0,
    totalPlanificatSaptamana: 0,
    totalPlanificatLunaCurenta: 0,
  });
  const [heatmapRange, setHeatmapRange] = useState("This month");
  const [heatmapData, setHeatmapData] = useState({});

  useEffect(() => {
    const maxPayments = 8;
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const simulatedData = {};
    for (let i = 1; i <= daysInMonth; i++) {
      simulatedData[i] = Math.floor(Math.random() * (maxPayments + 1)); // 0 - 8
    }
    setHeatmapData(simulatedData);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadPaymentPlannedCheck = async () => {
      setPaymentPlannedCheck((prev) => ({
        ...prev,
        loading: true,
        error: null,
      }));

      try {
        const response = await axios.get(
          `${TASKS_API_BASE_URL}/scadentar/payment-planned-check`
        );
        const summary = response.data?.summary || {};
        if (cancelled) {
          return;
        }
        setPaymentPlannedCheck({
          loading: false,
          healthy: Boolean(summary.healthy),
          totalProblems: Number(summary.totalProblems || 0),
          error: null,
        });
      } catch (error) {
        if (cancelled) {
          return;
        }
        setPaymentPlannedCheck({
          loading: false,
          healthy: false,
          totalProblems: 0,
          error:
            error?.response?.data?.error ||
            error?.message ||
            "Nu s-a putut valida reconcilierea APP.",
        });
      }
    };

    loadPaymentPlannedCheck();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    axios
      .get(`${TASKS_API_BASE_URL}/scadentar-list`)
      .then((res) => {
        const listRows = Array.isArray(res.data)
          ? res.data.map(normalizeListPaymentRow)
          : [];
        // Transformare pentru deadlinePlata depășit/planificat și ISO week
        const updatedRows = listRows.map((row) => {
          const deadline = new Date(row.deadlinePlata);
          const statusInitial = getPaymentStatus(row);
          // compute ISO week (UTC) from deadline if valid
          const week = !isNaN(deadline) ? isoWeekUTC(deadline) : null;

          if (!isNaN(deadline)) {
            if (deadline < new Date() && statusInitial === "Planificat") {
              return {
                ...row,
                saptamana: week,
                statusPlata: "Depasit",
                status: "Depasit",
              };
            }
            if (
              deadline >= new Date() &&
              (!statusInitial || statusInitial === "")
            ) {
              return {
                ...row,
                saptamana: week,
                statusPlata: "Planificat",
                status: "Planificat",
              };
            }
          }
          return { ...row, saptamana: week };
        });
        setRows(updatedRows);
        // Initialize statusFilter state with all active statuses.
        const uniqueStatuses = Array.from(
          new Set(updatedRows.map((r) => getPaymentStatus(r)))
        ).filter(Boolean);
        const filteredStatuses = uniqueStatuses
          .map((s) => s.toLowerCase())
          .filter((s) => !DEFAULT_HIDDEN_PAYMENT_STATUSES.has(s));
        setStatusFilter(filteredStatuses);
        // Trimite PATCH pentru fiecare rând unde statusul calculat diferă de cel existent
        updatedRows.forEach((row) => {
          const scadentarKey = getScadentarKey(row);
          const statusInitial = row.loadedStatusPlata ?? getPaymentStatus(row);

          // trimite PATCH dacă statusul calculat diferă de cel existent
          if (
            row.statusPlata === "Depasit" &&
            statusInitial !== "Depasit" &&
            scadentarKey
          ) {
            axios
              .patch(
                `${TASKS_API_BASE_URL}/scadentar/${scadentarKey}/status`,
                {
                  statusPlata: "Depasit",
                }
              )
              .catch((err) => {
                console.error(
                  `❌ Eroare la update status pentru ${scadentarKey}`,
                  err
                );
              });
          }

          if (
            row.statusPlata === "Planificat" &&
            statusInitial !== "Planificat" &&
            scadentarKey
          ) {
            axios
              .patch(
                `${TASKS_API_BASE_URL}/scadentar/${scadentarKey}/status`,
                {
                  statusPlata: "Planificat",
                }
              )
              .catch((err) => {
                console.error(
                  `❌ Eroare la update status pentru ${scadentarKey}`,
                  err
                );
              });
          }
        });
        setLoading(false);
      })
      .catch((err) => {
        console.error("Eroare la preluarea scadentarului:", err);
        setLoading(false);
      });

    axios
      .get(`${TASKS_API_BASE_URL}/scadentar-dashboard-summary`)
      .then((res) => {
        setStats(res.data);
      })
      .catch((err) => {
        console.error("Eroare la preluarea sumarelor:", err);
      });
  }, []);

  const formatRON = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = parseMoneyToNumber(value);
    if (num === null) return "-";
    return new Intl.NumberFormat("ro-RO", {
      style: "currency",
      currency: "RON",
    }).format(num);
  };

  // --- Helpers pentru extragere exacta din Detalii ---
  const parseMoneyToNumber = (raw) => {
    if (raw === null || raw === undefined || raw === "") return null;

    if (typeof raw === "number") {
      return Number.isFinite(raw) ? raw : null;
    }

    let str = String(raw)
      .replace(/RON|Lei|lei/gi, "")
      .replace(/\u00A0/g, " ")
      .replace(/\s+/g, "")
      .trim();

    const hasComma = str.includes(",");
    const hasDot = str.includes(".");
    if (hasComma && hasDot) {
      str = str.replace(/\./g, "").replace(",", ".");
    } else if (hasComma) {
      str = str.replace(",", ".");
    } else if (hasDot) {
      const parts = str.split(".");
      const lastPart = parts[parts.length - 1] || "";
      const looksLikeThousands =
        parts.length > 2 || (lastPart.length === 3 && parts[0].length > 1);
      if (looksLikeThousands) {
        str = str.replace(/\./g, "");
      }
    }

    const num = Number(str);
    return Number.isFinite(num) ? num : null;
  };

  // Helper for status color mapping
  const statusColor = (status) => {
    switch (status) {
      case "Creat":
        return "primary";
      case "Planificat":
        return "warning";
      case "Plătit":
      case "Achitat":
        return "success";
      case "Returnat":
        return "info";
      case "Retur Finalizat":
        return "success";
      case "Anulat":
        return "default";
      case "Whitelist":
        return "warning";
      case "Depasit":
        return "error";
      default:
        return "default";
    }
  };

  // Helper for status chip style (actualizat cu culori inspirate din design)
  const statusChipStyle = (status) => {
    switch (status) {
      case "Creat":
        return { backgroundColor: "#E3F2FD", color: "#1976D2" }; // albastru deschis
      case "Planificat":
        return { backgroundColor: "#FFF3E0", color: "#FB8C00" }; // portocaliu deschis
      case "Plătit":
      case "Achitat":
        return { backgroundColor: "#E8F5E9", color: "#388E3C" }; // verde pal
      case "Returnat":
        return { backgroundColor: "#F3E5F5", color: "#8E24AA" }; // mov pal
      case "Anulat":
        return { backgroundColor: "#ECEFF1", color: "#546E7A" }; // gri albastrui
      case "Whitelist":
        return { backgroundColor: "#FFF8E1", color: "#F9A825" }; // galben pal
      case "Retur Finalizat":
        return { backgroundColor: "#E8F5E9", color: "#2E7D32" }; // verde inchis
      case "Pending":
        return { backgroundColor: "#FFF8E1", color: "#F9A825" }; // galben pal
      case "Rejected":
        return { backgroundColor: "#FFEBEE", color: "#C62828" }; // roșu pal
      case "Paid":
        return { backgroundColor: "#E8F5E9", color: "#2E7D32" }; // verde plin
      case "Depasit":
        return { backgroundColor: "#FFEBEE", color: "#C62828" }; // roșu pal
      default:
        return {};
    }
  };

  // Dropdown pentru săptămâni - Menu (cu submenu)
  const [anchorEl, setAnchorEl] = useState(null);
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);

  const paymentCheckLabel = paymentPlannedCheck.loading
    ? "Verificare APP"
    : paymentPlannedCheck.error
      ? "Eroare APP"
      : paymentPlannedCheck.healthy
        ? "APP sincronizat"
        : "Diferențe APP";

  const paymentCheckColor = paymentPlannedCheck.loading
    ? {
        color: "#1d4ed8",
        borderColor: "rgba(59,130,246,0.28)",
        background:
          "linear-gradient(135deg, rgba(239,246,255,0.98), rgba(219,234,254,0.95))",
      }
    : paymentPlannedCheck.error || paymentPlannedCheck.healthy === false
      ? {
          color: "#b42318",
          borderColor: "rgba(244,63,94,0.28)",
          background:
            "linear-gradient(135deg, rgba(255,241,242,0.98), rgba(255,228,230,0.95))",
        }
      : {
          color: "#027a48",
          borderColor: "rgba(16,185,129,0.24)",
          background:
            "linear-gradient(135deg, rgba(236,253,245,0.98), rgba(209,250,229,0.94))",
        };

  const paymentCheckIcon = paymentPlannedCheck.loading ? (
    <SyncRounded sx={{ fontSize: 18, animation: "scadentarSpin 1.2s linear infinite" }} />
  ) : paymentPlannedCheck.error || paymentPlannedCheck.healthy === false ? (
    <CancelOutlined sx={{ fontSize: 18 }} />
  ) : (
    <TaskAlt sx={{ fontSize: 18 }} />
  );

  return (
    <Box p={3}>
      {/* New Table Layout */}
      <Box mt={5} display="flex" alignItems="flex-start">
        <Paper
          sx={{
            borderRadius: 2,
            boxShadow: 3,
            flexGrow: 1,
            transition: "width 0.3s",
            width: panelOpen ? "60%" : "100%",
          }}
        >
          {/* Title bar with icon, search and actions */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={3}
            py={2}
            sx={{ borderBottom: "1px solid #f0f0f0", background: "#fafbfc" }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Assignment sx={{ color: "#1976d2" }} fontSize="medium" />
              <Typography variant="h6" fontWeight="bold">
                Scadentar Luxura Boutique
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {/* Săptămână filter with nested submenu */}
              <Box
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  p: 0.5,
                  position: "relative",
                }}
              >
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                  <FilterList fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  MenuListProps={{ dense: true }}
                >
                  <MenuItem
                    onMouseEnter={(e) => setSubmenuAnchorEl(e.currentTarget)}
                    onMouseLeave={(e) => {
                      /* keep open until submenu closes */
                    }}
                    onClick={(e) => setSubmenuAnchorEl(e.currentTarget)}
                    sx={{
                      fontSize: "0.85rem",
                      display: "flex",
                      justifyContent: "space-between",
                      minWidth: 180,
                    }}
                    aria-haspopup="true"
                    aria-controls="week-submenu"
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <FilterList fontSize="small" />
                      Săptămâna
                    </Box>
                    <ArrowRight fontSize="small" />
                  </MenuItem>
                  {/* Keep Menu open while submenu is open */}
                </Menu>
                <Menu
                  id="week-submenu"
                  anchorEl={submenuAnchorEl}
                  open={Boolean(submenuAnchorEl)}
                  onClose={() => setSubmenuAnchorEl(null)}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  MenuListProps={{
                    dense: true,
                    onMouseLeave: () => setSubmenuAnchorEl(null),
                  }}
                >
                  <MenuItem
                    onClick={() => {
                      setWeekFilter([]);
                      setAnchorEl(null);
                      setSubmenuAnchorEl(null);
                    }}
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: weekFilter.length === 0 ? "bold" : undefined,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <input
                        type="checkbox"
                        checked={weekFilter.length === 0}
                        readOnly
                      />
                      Toate săptămânile
                    </Box>
                  </MenuItem>
                  {availableWeeks.map((week) => (
                    <MenuItem
                      key={week}
                      onClick={() => {
                        setWeekFilter((prev) =>
                          prev.includes(week)
                            ? prev.filter((w) => w !== week)
                            : [...prev, week]
                        );
                      }}
                      sx={{ fontSize: "0.85rem" }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <input
                          type="checkbox"
                          checked={weekFilter.includes(week)}
                          readOnly
                        />
                        Săptămâna {week}
                      </Box>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
              <Box
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  p: 0.5,
                }}
              >
                <IconButton size="small" color="inherit">
                  <ImportExport fontSize="small" />
                </IconButton>
              </Box>
              {/* Status filter */}
              <Box
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: 2,
                  backgroundColor: "#fff",
                  p: 0.5,
                }}
              >
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
                >
                  <Sort fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={statusMenuAnchor}
                  open={Boolean(statusMenuAnchor)}
                  onClose={() => setStatusMenuAnchor(null)}
                >
                  {availableStatuses.map((status) => {
                    const value = status.toLowerCase();
                    return (
                      <MenuItem
                        key={value}
                        selected={statusFilter.includes(value)}
                        onClick={() => {
                          setStatusFilter((prev) =>
                            prev.includes(value)
                              ? prev.filter((s) => s !== value)
                              : [...prev, value]
                          );
                        }}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <input
                            type="checkbox"
                            checked={statusFilter.includes(value)}
                            readOnly
                          />
                          <Chip
                            label={status}
                            size="small"
                            sx={{
                              fontWeight: 500,
                              fontSize: "0.75rem",
                              ...statusChipStyle(status),
                            }}
                          />
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Menu>
              </Box>
              <Box>
                <TextField
                  size="small"
                  placeholder="Caută..."
                  variant="outlined"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{
                    minWidth: 160,
                    "& .MuiOutlinedInput-root": { borderRadius: 2 },
                  }}
                />
              </Box>
              <Tooltip
                title={
                  paymentPlannedCheck.error ||
                  (paymentPlannedCheck.loading
                    ? "Verific reconcilierea cu APP..."
                    : paymentPlannedCheck.healthy
                      ? "Datele din APP coincid cu scadentarul."
                      : `Există ${paymentPlannedCheck.totalProblems} produse cu diferențe între APP și scadentar.`)
                }
              >
                <Box
                  onClick={() => navigate("/scadentar/payment-planned-check")}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 0.9,
                    borderRadius: "999px",
                    border: "1px solid",
                    cursor: "pointer",
                    minWidth: 176,
                    justifyContent: "space-between",
                    transition:
                      "transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease",
                    boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
                    ...paymentCheckColor,
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: "0 14px 30px rgba(15,23,42,0.12)",
                    },
                    "@keyframes scadentarSpin": {
                      from: { transform: "rotate(0deg)" },
                      to: { transform: "rotate(360deg)" },
                    },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Badge
                      color="error"
                      overlap="circular"
                      badgeContent={
                        !paymentPlannedCheck.loading &&
                        !paymentPlannedCheck.healthy &&
                        !paymentPlannedCheck.error &&
                        paymentPlannedCheck.totalProblems > 0
                          ? paymentPlannedCheck.totalProblems
                          : 0
                      }
                      invisible={
                        paymentPlannedCheck.loading ||
                        paymentPlannedCheck.healthy ||
                        Boolean(paymentPlannedCheck.error) ||
                        paymentPlannedCheck.totalProblems <= 0
                      }
                      sx={{
                        "& .MuiBadge-badge": {
                          fontWeight: 800,
                          minWidth: 22,
                          height: 22,
                          borderRadius: "999px",
                          boxShadow: "0 0 0 2px rgba(255,255,255,0.95)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: "999px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "rgba(255,255,255,0.72)",
                        }}
                      >
                        {paymentCheckIcon}
                      </Box>
                    </Badge>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, letterSpacing: "0.02em" }}
                    >
                      {paymentCheckLabel}
                    </Typography>
                  </Box>
                </Box>
              </Tooltip>
            </Box>
          </Box>
          {/* Head row with sortables and search */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            px={3}
            py={1}
            sx={{ borderBottom: "1px solid #f0f0f0", background: "#f5f5f7" }}
          >
            <Box display="flex" width="100%">
              {/* Vânzător header - now first */}
              <Box
                flex={1}
                sx={{
                  textAlign: "right",
                  overflow: "hidden",
                  minWidth: 0,
                  px: 1,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    userSelect: "none",
                    justifyContent: "flex-end",
                  }}
                  onClick={() => handleSort("vanzator")}
                >
                  Vânzător
                  {sortConfig.key === "vanzator" &&
                    (sortConfig.direction === "asc" ? (
                      <ArrowUpward fontSize="small" />
                    ) : (
                      <ArrowDownward fontSize="small" />
                    ))}
                </Typography>
              </Box>
              {/* Nr Jurnal header */}
              <Box
                flex={1}
                sx={{
                  textAlign: "right",
                  overflow: "hidden",
                  minWidth: 0,
                  px: 1,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    userSelect: "none",
                    justifyContent: "flex-end",
                    pr: 3,
                  }}
                  onClick={() => handleSort("nr_jurnal")}
                >
                  Nr Jurnal
                  {sortConfig.key === "nr_jurnal" &&
                    (sortConfig.direction === "asc" ? (
                      <ArrowUpward fontSize="small" />
                    ) : (
                      <ArrowDownward fontSize="small" />
                    ))}
                </Typography>
              </Box>
              {/* Produs header - now in Vânzător's previous position */}
              <Box
                flex={2}
                sx={{
                  textAlign: "left",
                  overflow: "hidden",
                  minWidth: 0,
                  px: 1,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    userSelect: "none",
                  }}
                  onClick={() => handleSort("produs")}
                >
                  Produs
                  {sortConfig.key === "produs" &&
                    (sortConfig.direction === "asc" ? (
                      <ArrowUpward fontSize="small" />
                    ) : (
                      <ArrowDownward fontSize="small" />
                    ))}
                </Typography>
              </Box>
              {/* Preț Intrare header */}
              <Box
                flex={1}
                sx={{
                  textAlign: "right",
                  overflow: "hidden",
                  minWidth: 0,
                  px: 1,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    userSelect: "none",
                    justifyContent: "flex-end",
                  }}
                  onClick={() => handleSort("deadlinePlata")}
                >
                  Zi Plata
                  {sortConfig.key === "deadlinePlata" &&
                    (sortConfig.direction === "asc" ? (
                      <ArrowUpward fontSize="small" />
                    ) : (
                      <ArrowDownward fontSize="small" />
                    ))}
                </Typography>
              </Box>
              {/* Suma de Plata header */}
              <Box
                flex={1}
                sx={{
                  textAlign: "right",
                  overflow: "hidden",
                  minWidth: 0,
                  px: 1,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    userSelect: "none",
                    justifyContent: "flex-end",
                  }}
                >
                  Sumă de plată
                </Typography>
              </Box>
              {/* Status header */}
              <Box
                flex={1}
                sx={{
                  textAlign: "right",
                  overflow: "hidden",
                  minWidth: 0,
                  px: 1,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    userSelect: "none",
                    justifyContent: "flex-end",
                  }}
                  onClick={() => handleSort("status")}
                >
                  Status
                  {sortConfig.key === "status" &&
                    (sortConfig.direction === "asc" ? (
                      <ArrowUpward fontSize="small" />
                    ) : (
                      <ArrowDownward fontSize="small" />
                    ))}
                </Typography>
              </Box>
              {/* Removed Acțiune header */}
            </Box>
          </Box>
          {/* Table rows */}
          {loading ? (
            <Box textAlign="center" py={5}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              onScroll={handleRowsScroll}
              sx={{
                px: 7,
                maxHeight: "calc(90vh)",
                overflowY: "auto",
              }}
            >
              {visibleRows && visibleRows.length > 0 ? (
                visibleRows.map((row) => {
                  const isSelected =
                    selectedRow &&
                    getScadentarKey(selectedRow) === getScadentarKey(row);
                  return (
                    <Box
                      key={getScadentarKey(row)}
                      display="flex"
                      alignItems="center"
                      py={2}
                      onClick={() => {
                        setSelectedRow(row);
                        setPanelOpen(true);
                      }}
                      sx={{
                        cursor: "pointer",
                        borderBottom: "1px solid #f0f0f0",
                        "&:last-child": { borderBottom: 0 },
                        background: isSelected ? "#e3f2fd" : "#fff",
                        boxShadow: isSelected
                          ? "0 2px 8px rgba(0,0,0,0.15)"
                          : "none",
                        transition: "background 0.2s, box-shadow 0.2s",
                        "&:hover": {
                          background: isSelected ? "#e3f2fd" : "#f9f9fa",
                        },
                      }}
                    >
                      {/* Vânzător - now first */}
                      <Box
                        flex={1}
                        sx={{
                          textAlign: "right",
                          overflow: "hidden",
                          minWidth: 0,
                          px: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ fontWeight: isSelected ? "bold" : undefined }}
                        >
                          {row.customfield_10067 || row.vanzator || "-"}
                        </Typography>
                      </Box>
                      {/* Nr Jurnal */}
                      <Box
                        flex={1}
                        sx={{
                          textAlign: "right",
                          overflow: "hidden",
                          minWidth: 0,
                          px: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            justifyContent: "flex-end",
                            pr: 3,
                            display: "flex",
                            fontWeight: isSelected ? "bold" : undefined,
                          }}
                        >
                          {row.customfield_10066 || row.nr_jurnal || "-"}
                        </Typography>
                      </Box>
                      {/* Produs - now in Vânzător's previous position */}
                      <Box
                        flex={2}
                        sx={{
                          textAlign: "left",
                          overflow: "hidden",
                          minWidth: 0,
                          px: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ fontWeight: isSelected ? "bold" : undefined }}
                        >
                          {row.summary || row.produs}
                        </Typography>
                      </Box>
                      {/* Preț Intrare */}
                      <Box
                        flex={1}
                        sx={{
                          textAlign: "right",
                          overflow: "hidden",
                          minWidth: 0,
                          px: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            justifyContent: "flex-end",
                            pr: 0,
                            display: "flex",
                            fontWeight: isSelected ? "bold" : undefined,
                          }}
                        >
                          {row.deadlinePlata ? row.deadlinePlata : "-"}
                        </Typography>
                      </Box>
                      {/* Sumă de plată */}
                      <Box
                        flex={1}
                        sx={{
                          textAlign: "right",
                          overflow: "hidden",
                          minWidth: 0,
                          px: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            justifyContent: "flex-end",
                            pr: 0,
                            display: "flex",
                            fontWeight: isSelected ? "bold" : undefined,
                          }}
                        >
                          {(() => {
                            const now = new Date();
                            const contractCode = (
                              row.customfield_10183_value ||
                              row.tip_contract ||
                              ""
                            )
                              .toString()
                              .toUpperCase();
                            const lp002Matched = contractCode.includes("LP002");
                            if (lp002Matched) {
                              const dueRaw = row.customfield_10194;
                              const dueDate = dueRaw ? new Date(dueRaw) : null;
                              const dueValid =
                                dueDate instanceof Date &&
                                !Number.isNaN(dueDate.getTime());
                              if (dueValid && now >= dueDate) {
                                const lpAmount = parseMoneyToNumber(
                                  row.customfield_10195
                                );
                                if (lpAmount != null) {
                                  return formatRON(lpAmount);
                                }
                              }
                            }
                            // 1) Exact aceeași logică ca în ScadentarDetails
                            const from10181 = parseMoneyToNumber(
                              row.customfield_10181
                            );
                            const from10072 = parseMoneyToNumber(
                              row.customfield_10072
                            );
                            const fromPretIntrare = parseMoneyToNumber(
                              row.pret_intrare
                            );
                            const exactDetailsLogic =
                              from10181 != null
                                ? from10181
                                : from10072 != null
                                  ? from10072
                                  : fromPretIntrare != null
                                    ? fromPretIntrare
                                    : null;

                            // 2) Fallback: suma calculată din endpointul de detalii (batch/per-item)
                            const primary = row.sumaPlataExacta;

                            // 3) Fallback: suma normalizată de server în lista /scadentar-list
                            const secondary = row.sumaPlata;

                            // 4) Fallback minimal pe chei legacy
                            const fallback =
                              row.sumaDePlata ??
                              row.suma_de_plata ??
                              row.suma_plata ??
                              row.sumaTotala ??
                              row.suma_totala ??
                              row.valoare ??
                              row.total ??
                              null;

                            const value =
                              exactDetailsLogic != null
                                ? exactDetailsLogic
                                : primary != null
                                  ? primary
                                  : secondary != null
                                    ? secondary
                                    : fallback;
                            return formatRON(value);
                          })()}
                        </Typography>
                      </Box>
                      {/* Status */}
                      <Box
                        flex={1}
                        sx={{
                          textAlign: "right",
                          overflow: "hidden",
                          minWidth: 0,
                          px: 1,
                        }}
                      >
                        <Chip
                          label={getPaymentStatus(row) || "-"}
                          size="small"
                          sx={{
                            fontWeight: isSelected ? "bold" : 500,
                            fontSize: "0.85rem",
                            ...statusChipStyle(getPaymentStatus(row)),
                          }}
                        />
                      </Box>
                      {/* Removed Acțiune */}
                    </Box>
                  );
                })
              ) : (
                <Box textAlign="center" py={4}>
                  <Typography variant="body2" color="textSecondary">
                    {isSearchActive
                      ? `Nu există rezultate pentru "${searchTerm}".`
                      : "Nu există date disponibile."}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Paper>
        {panelOpen && selectedRow && (
          // No wrapper or style/sx with overflowY: 'auto' here!
          <ScadentarDetails
            selectedRow={selectedRow}
            onClose={() => setPanelOpen(false)}
            onNotify={({ message, severity = "success" }) => {
              setPageSnackbar({
                open: true,
                message,
                severity,
              });
            }}
            onDeleted={({ id, message }) => {
              setRows((prev) =>
                prev.filter((row) => getScadentarKey(row) !== id)
              );
              setSelectedRow((prev) =>
                prev && getScadentarKey(prev) === id ? null : prev
              );
              setPanelOpen(false);
              setPageSnackbar({
                open: true,
                message:
                  message ||
                  "Planificarea la plată din scadentar a fost ștearsă.",
                severity: "success",
              });
            }}
            onUpdate={(updatedRow) => {
              const id =
                getScadentarKey(updatedRow) || getScadentarKey(selectedRow);

              const newStatus = getPaymentStatus(updatedRow);

              setRows((prev) => {
                return prev.map((r) => {
                  const same =
                    getScadentarKey(r) === id;
                  if (!same) return r;
                  const paymentStatus = newStatus || getPaymentStatus(r);
                  const merged = { ...r, ...updatedRow };
                  if (paymentStatus) {
                    merged.statusPlata = paymentStatus;
                    merged.status = paymentStatus;
                  }
                  return merged;
                });
              });

              setSelectedRow((prev) => {
                if (!prev) return prev;
                const same =
                  getScadentarKey(prev) === id;
                if (!same) return prev;
                const paymentStatus = newStatus || getPaymentStatus(prev);
                const merged = { ...prev, ...updatedRow };
                if (paymentStatus) {
                  merged.statusPlata = paymentStatus;
                  merged.status = paymentStatus;
                }
                return merged;
              });

              // Background refetch for freshest data, then re-normalize both fields again
              axios
                .get(`${TASKS_API_BASE_URL}/scadentar/${id}`)
                .then((res) => {
                  const freshRow = res.data || {};
                  const statusFromServer = getPaymentStatus(freshRow);
                  setRows((prev) => {
                    return prev.map((r) => {
                      const same =
                        getScadentarKey(r) === id;
                      if (!same) return r;
                      const paymentStatus =
                        statusFromServer || getPaymentStatus(r) || newStatus;
                      const merged = {
                        ...r,
                        ...freshRow,
                        jiraStatus:
                          freshRow.jiraStatus ?? freshRow.status ?? r.jiraStatus,
                      };
                      if (paymentStatus) {
                        merged.statusPlata = paymentStatus;
                        merged.status = paymentStatus;
                      }
                      return merged;
                    });
                  });

                  setSelectedRow((prev) => {
                    if (!prev) {
                      const paymentStatus = statusFromServer || newStatus;
                      return paymentStatus
                        ? {
                            ...freshRow,
                            jiraStatus:
                              freshRow.jiraStatus ?? freshRow.status ?? null,
                            statusPlata: paymentStatus,
                            status: paymentStatus,
                          }
                        : freshRow;
                    }
                    const same =
                      getScadentarKey(prev) === id;
                    if (!same) return prev;
                    const paymentStatus =
                      statusFromServer || getPaymentStatus(prev) || newStatus;
                    const merged = {
                      ...prev,
                      ...freshRow,
                      jiraStatus:
                        freshRow.jiraStatus ?? freshRow.status ?? prev.jiraStatus,
                    };
                    if (paymentStatus) {
                      merged.statusPlata = paymentStatus;
                      merged.status = paymentStatus;
                    }
                    return merged;
                  });
                })
                .catch((err) => {
                  console.error("❌ Eroare la refetch task după update:", err);
                });
            }}
          />
        )}
        <Snackbar
          open={pageSnackbar.open}
          autoHideDuration={3500}
          onClose={handleClosePageSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleClosePageSnackbar}
            severity={pageSnackbar.severity}
            sx={{ width: "100%" }}
          >
            {pageSnackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default Scadentar;
